import {AfterViewInit, Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {LoaderService} from '../../../core/services/loader.service';
import {plainToClass} from 'class-transformer';
import {CredentialsApiModel} from '../../../api/models/credentials.api.model';
import {TokenDataProviderService} from '../../../core/services/token-data-provider.service';
import { HttpErrorResponse } from '@angular/common/http';
import {Capacitor} from '@capacitor/core';
import {FacebookService} from "../../../core/services/facebook.service";
import {AppTrackingTransparency} from "capacitor-plugin-app-tracking-transparency";
import {GoogleService} from "../../../core/services/google.service";
import {RegistrationRestService} from "../../../api/services/registration.rest.service";
import {MessageApiModel} from "../../../api/models/message.api.model";
import {ResponseNotificationService} from "../../../core/services/response-notification.service";
import {TranslateService} from "@ngx-translate/core";
import {AuthReturnUrlService} from '../../../core/services/auth-return-url.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, AfterViewInit {

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private ts: TokenDataProviderService,
    private ls: LoaderService,
    private fs: FacebookService,
    private gs: GoogleService,
    private regService: RegistrationRestService,
    private rns: ResponseNotificationService,
    private translate: TranslateService,
    private readonly returnUrls: AuthReturnUrlService,
  ) {
    this.createForm();
  }

  public loginForm: UntypedFormGroup;
  public error: string;
  public showInactiveAccountCta = false;
  public isResendingActivation = false;

  public readonly Capacitor = Capacitor;
  async ngOnInit() {
    this.returnUrls.remember(this.route.snapshot.queryParamMap.get('returnUrl'));
  }

  async ngAfterViewInit() {
  }


  currentStyles() {
    return {
      background: 'url(/uploads/backgrounds/test1.jpg) no-repeat fixed center',
      'background-size': 'cover',
    };
  }

  createForm() {
    this.loginForm = this.fb.group(
      {
        username: ['', Validators.required],
        password: ['', Validators.required],
        remember: [true],
      }
    );
  }

  login() {
    this.showInactiveAccountCta = false;
    this.ls.show();
    this.ts.login(plainToClass(CredentialsApiModel, this.loginForm.value as object))
      .subscribe(response => {
        this.router.navigateByUrl(this.getReturnUrl());
      },
        (errorResponse: HttpErrorResponse) => {
          this.showInactiveAccountCta = errorResponse.error?.code === 'USER_NOT_ENABLED';
      }
    ).add(() => this.ls.hide());
  }

  resendActivationEmail(): void {
    const usernameOrEmail = (this.loginForm.get('username')?.value || '').trim();

    if (usernameOrEmail === '' || this.isResendingActivation) {
      this.rns.emitError(this.translate.instant('auth.login.inactive_account.identifier_required'));
      return;
    }

    this.isResendingActivation = true;
    this.ls.show();
    this.regService.resendRegistrationConfirmation(usernameOrEmail)
      .subscribe({
        next: (response: MessageApiModel) => {
          this.rns.emitMessage(response.message);
        }
      })
      .add(() => {
        this.isResendingActivation = false;
        this.ls.hide();
      });
  }

  async fbLogin() {
    await this.fs.initFacebook()
    let limited = false;

    if (Capacitor.getPlatform() === 'ios') {
      const response = await AppTrackingTransparency.getStatus();
      limited = response.status !== 'authorized';
    }

    this.ls.show();
    this.ts.fbLogin(this.getFormValue(), limited)
      .subscribe(() => {
        this.router.navigateByUrl(this.getReturnUrl());
      },
        (_errorResponse: HttpErrorResponse) => {
        }
      ).add(() => this.ls.hide());
  }

  async googleLogin() {
    await this.gs.init()

    this.ls.show();
    this.ts.googleLogin(this.getFormValue())
      .subscribe(() => {
          this.router.navigateByUrl(this.getReturnUrl());
        },
        (_errorResponse: HttpErrorResponse) => {
        }
      ).add(() => this.ls.hide());
  }

  private getFormValue(): CredentialsApiModel {
    return plainToClass(CredentialsApiModel, this.loginForm.value as object);
  }

  private getReturnUrl(): string {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    // OAuth accounts may still need the registration completion screen.
    if (this.ts.requiresRegistrationCompletion()) {
      const destination = this.returnUrls.resolve(returnUrl);
      this.returnUrls.remember(destination);
      return destination;
    }
    return this.returnUrls.consume(returnUrl);
  }
}
